import subDays from 'date-fns/subDays'
import { observer } from 'mobx-react'
import React, { FC } from 'react'

import {
  formatDate,
  HorizontalAlignment,
  parseISODate,
  SwitchButton,
  useIsTabletOrMobile,
} from '@avantstay/backoffice-core'

import { DatePickerBasedFilter } from '@new/__global/components/Filters/FiltersVariations/DatePicker'
import { FilterRenderingComponentProps } from '@new/__global/components/FiltersFramework'
import { filtersLabels } from '@new/domains/bookings/screens/bookingsList/utils/labels'

import { AvailableDatesRange } from '../../__types'
import { formatDateRange, parseDateRange } from '../../__utils'
import { ISO_DATE_FORMAT, TIME_FRAME_DAYS } from '../../config/contants'
import { storeHolder } from '../../stores/TapeChartStore'
import * as S from './TapeChartFilters.styles'

const _AvailableDatesFilter: FC<FilterRenderingComponentProps<AvailableDatesRange>> = ({
  activeValue,
  label,
  onChange,
  isAccordionOpen,
  isSidebarFilter,
}) => {
  const rootStore = storeHolder.store
  const isTabletOrMobile = useIsTabletOrMobile()

  const getDateRangeObject = (value?: string): AvailableDatesRange | undefined => {
    const parsedRange = value ? parseDateRange(value) : undefined

    if (!parsedRange) {
      return undefined
    }

    return {
      ...parsedRange,
      ignoreBlocks: activeValue?.ignoreBlocks || false,
    }
  }

  const pushChange = (value?: AvailableDatesRange) => {
    onChange(value)

    // checking if we should change viewFrom (if selected range is outside of current tapeChart rage
    if (value) {
      const { startDate, endDate, setViewFrom } = rootStore.filterStore
      const start = formatDate(startDate, ISO_DATE_FORMAT)
      const end = formatDate(endDate, ISO_DATE_FORMAT)
      const { checkIn, checkOut } = value
      if (!(start < checkIn && end > checkOut)) {
        setViewFrom?.(subDays(parseISODate(checkIn), TIME_FRAME_DAYS / 2))
      }
    }
  }

  const handleRangeChange = (value?: string) => {
    pushChange(getDateRangeObject(value))
  }
  const handleSwitchChange = () => {
    if (activeValue) {
      pushChange({
        ...activeValue,
        ignoreBlocks: !activeValue.ignoreBlocks,
      })
    }
  }

  return (
    <DatePickerBasedFilter
      title={filtersLabels.availableDates}
      initialValue={activeValue ? formatDateRange(activeValue) : undefined}
      placeholder="Any date"
      onChange={handleRangeChange}
      labelInternal={label}
      shouldAcceptEmptyDate
      clearButtonLabel={isTabletOrMobile ? 'Reset' : 'Clear'}
      horizontalAlignment={isTabletOrMobile ? HorizontalAlignment.Right : HorizontalAlignment.Left}
      inputWidthMaxContent
      isAccordionOpen={isAccordionOpen}
      isSidebarFilter={isSidebarFilter}
      children={
        <S.SwitchContainer>
          <SwitchButton
            id="availableDatesSwitch"
            onChange={handleSwitchChange}
            checked={activeValue?.ignoreBlocks}
          />
          <span>
            Show properties with <b>blocks</b> for selected period
          </span>
        </S.SwitchContainer>
      }
    />
  )
}

export const AvailableDatesFilter = observer(_AvailableDatesFilter)
